import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import addressApi from "api/landlord/address";
import { ScreeningSettingsForm } from "shared/app/ScreeningSettingsForm";

interface EditScreeningSettingsForm {
  hasPropertyGroup: boolean;
  propertyId: number;
  verificationTemplateId?: string;
  usePropertyGroupVerificationTemplate: boolean;
}

const EditScreeningSettingsForm = ({
  hasPropertyGroup,
  propertyId,
  verificationTemplateId,
  usePropertyGroupVerificationTemplate
}: EditScreeningSettingsForm) => {
  const queryClient = useQueryClient();

  const { mutate: editScreeningSettings, isPending } = useMutation({
    mutationFn: ({
      verificationTemplateId,
      usePropertyGroupVerificationTemplate
    }: {
      verificationTemplateId?: string;
      usePropertyGroupVerificationTemplate?: boolean;
    }) => {
      return addressApi.editProperty({
        propertyId,
        verification_template_id: verificationTemplateId,
        use_property_group_verification_template:
          usePropertyGroupVerificationTemplate
      });
    },
    onSuccess: () => {
      toast.success("Screening settings updated");
      queryClient.invalidateQueries({
        queryKey: ["properties", String(propertyId)]
      });
    },
    onError: (error) => {
      toast.error(
        "Failed to update screening settings",
        getErrorMessage(error)
      );
    }
  });

  return (
    <ScreeningSettingsForm
      key="edit-modal"
      hasPropertyGroup={hasPropertyGroup}
      selectedTemplateId={verificationTemplateId}
      useGroupTemplate={usePropertyGroupVerificationTemplate}
      onTemplateSelect={(id) =>
        editScreeningSettings({
          verificationTemplateId: id
        })
      }
      onUseGroupTemplateChange={(checked) =>
        editScreeningSettings({
          usePropertyGroupVerificationTemplate: checked
        })
      }
      isUpdating={isPending}
    />
  );
};

interface ScreeningSettingsSectionProps {
  hasPropertyGroup: boolean;
  verificationTemplateId?: string;
  usePropertyGroupVerificationTemplate: boolean;
  propertyId: number;
}

export const ScreeningSettingsSection = ({
  hasPropertyGroup,
  verificationTemplateId,
  usePropertyGroupVerificationTemplate,
  propertyId
}: ScreeningSettingsSectionProps) => {
  return (
    <EditScreeningSettingsForm
      hasPropertyGroup={hasPropertyGroup}
      propertyId={propertyId}
      verificationTemplateId={verificationTemplateId}
      usePropertyGroupVerificationTemplate={
        usePropertyGroupVerificationTemplate
      }
    />
  );
};
